import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../ui/card";
import PlaneImg from "../../assets/AVIAO.svg"; // <-- sua imagem

// Se preferir manter o mesmo nome (TimelineSection), pode renomear.
// Aqui uso um nome específico para evitar conflito com o rodoviário.
export default function AeroviarioTimelineSection({ timeline }) {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [planePos, setPlanePos] = useState({ left: 0, top: 0 });

  const sectionRef = useRef(null);
  const trackWrapRef = useRef(null);
  const trackRef = useRef(null);
  const trackCenterRef = useRef(null);
  const planeRef = useRef(null);
  const cardRefs = useRef([]);

  // Detecta se é mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lógica de scroll + posição do avião (centralizado na tela)
  useEffect(() => {
    cardRefs.current = timeline.map(() => React.createRef());

    const update = () => {
      if (
        !sectionRef.current ||
        !trackWrapRef.current ||
        !trackRef.current ||
        !planeRef.current ||
        !trackCenterRef.current
      ) {
        return;
      }

      // Progresso da seção (para revelar cards gradualmente)
      const sec = sectionRef.current.getBoundingClientRect();
      const secTop = sec.top + window.scrollY;
      const secH = sec.height;
      const vh = window.innerHeight;

      const start = secTop - vh + 200;
      const end = secTop + secH - 200;
      const progress = Math.max(0, Math.min(1, (window.scrollY - start) / (end - start)));

      const newVisible = new Set();
      const itemsToShow = Math.floor(progress * timeline.length * 1.2);
      for (let i = 0; i < Math.min(itemsToShow, timeline.length); i++) newVisible.add(i);
      setVisibleItems(newVisible);

      // Centraliza o avião verticalmente no "céu" acompanhando o centro da viewport
      const wrapRect = trackWrapRef.current.getBoundingClientRect();
      const trackRect = trackRef.current.getBoundingClientRect();
      const centerRect = trackCenterRef.current.getBoundingClientRect();

      const planeH = planeRef.current.offsetHeight || 0;

      const trackTopGlobal = trackRect.top + window.scrollY;
      const centerOfViewport = window.scrollY + vh / 2;

      let p = (centerOfViewport - trackTopGlobal) / trackRect.height; // 0 => topo do track, 1 => base
      p = Math.max(0, Math.min(1, p));

      const trackTopInWrap = trackRect.top - wrapRect.top;
      const usableH = Math.max(0, trackRect.height - planeH);

      // Leve compensação vertical no mobile para não cobrir demasiadamente os cards
      const MOBILE_OFFSET_Y = 160;
      const extraY = isMobile ? MOBILE_OFFSET_Y : 0;

      let topInWrap = trackTopInWrap + usableH * p + extraY;
      const minTop = trackTopInWrap;
      const maxTop = trackTopInWrap + usableH;
      topInWrap = Math.max(minTop, Math.min(maxTop, topInWrap));

      if (isMobile) {
        // No mobile mantemos no centro horizontal (50%) e só movemos o top
        setPlanePos((prev) => ({ left: prev.left, top: Math.round(topInWrap) }));
      } else {
        // No desktop, posiciona exatamente no centro do "trilho do céu"
        const centerXInWrap = centerRect.left - wrapRect.left;
        setPlanePos({ left: Math.round(centerXInWrap), top: Math.round(topInWrap) });
      }
    };

    const onScroll = () => update();
    const onResize = () => update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    requestAnimationFrame(update);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [timeline, isMobile]);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-sky-50 to-white relative overflow-x-clip"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Marcos do Transporte Aéreo 2024
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Acompanhe a jornada dos principais acontecimentos do setor aeroviário brasileiro
          </p>
        </div>

        <div ref={trackWrapRef} className="relative">
          {/* Trilho do céu (coluna central) */}
          <div
            ref={trackRef}
            className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-28 md:w-32 
                       bg-gradient-to-b from-sky-200 via-sky-100 to-sky-50 shadow-lg 
                       rounded-full opacity-90"
          >
            {/* Nuvens decorativas */}
            <div className="absolute left-2 top-[10%] w-10 h-5 bg-white rounded-full opacity-70"></div>
            <div className="absolute right-2 top-[22%] w-8 h-4 bg-white rounded-full opacity-60"></div>
            <div className="absolute left-4 top-[37%] w-12 h-6 bg-white rounded-full opacity-75"></div>
            <div className="absolute right-3 top-[53%] w-9 h-4 bg-white rounded-full opacity-65"></div>
            <div className="absolute left-3 top-[69%] w-11 h-5 bg-white rounded-full opacity-70"></div>
            <div className="absolute right-4 top-[84%] w-8 h-4 bg-white rounded-full opacity-60"></div>

            {/* Linha invisível marcando o centro (para cálculo do X) */}
            <div
              ref={trackCenterRef}
              className="absolute left-1/2 top-0 w-px h-full pointer-events-none"
            />
          </div>

          {/* Avião (SVG importado) */}
          <div
            ref={planeRef}
            className={`${isMobile ? "z-10" : "z-20"} 
                        absolute transition-[top,left] duration-150 ease-out`}
            style={{
              left: isMobile ? "50%" : `${planePos.left}px`,
              top: `${planePos.top}px`,
            }}
          >
          <div className="-translate-x-1/2 w-36 h-20 md:w-44 md:h-24 drop-shadow-xl">
  <img
    src={PlaneImg}
    alt="Avião"
    className="w-full h-full object-contain pointer-events-none select-none"
  />
</div>

          </div>

          {/* Itens da timeline */}
          <div className={`${isMobile ? "space-y-0" : "space-y-4"} relative ${isMobile ? "z-20" : ""}`}>
            {timeline.map((item, index) => {
              const isLeft = index % 2 === 0;
              const isVisible = visibleItems.has(index);

              // Layout Mobile: cards em blocos altos para o avião ficar sempre “no meio” visualmente
              if (isMobile) {
                return (
                  <div key={index} className="min-h-[80vh] flex items-center justify-center">
                    <div
                      ref={cardRefs.current[index]}
                      className={`w-[90%] max-w-md transition-all duration-500 ease-out ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                      }`}
                    >
                      <Card className="shadow-xl border-0 bg-white/85 backdrop-blur-sm">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-14 h-14 bg-gradient-to-br from-sky-100 to-sky-200 rounded-xl flex items-center justify-center text-3xl shadow border border-sky-200">
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <span className="bg-gradient-to-r from-sky-600 to-sky-700 text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow inline-block mb-3">
                                {item.month}
                              </span>
                              <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                                {item.title}
                              </h3>
                              <p className="text-gray-700 leading-relaxed">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                );
              }

              // Layout Desktop: alterna esquerda/direita e liga ao trilho com linha/pino
              return (
                <div
                  key={index}
                  className={`flex items-center min-h-[250px] ${isLeft ? "justify-start" : "justify-end"}`}
                >
                  <div
                    ref={cardRefs.current[index]}
                    className={`w-1/2 transition-all duration-700 ease-in-out ${
                      isVisible ? "opacity-100 translate-x-0" : `opacity-0 ${isLeft ? "-translate-x-10" : "translate-x-10"}`
                    }`}
                  >
                    <div className={`relative ${isLeft ? "pr-24" : "pl-24"}`}>
                      {/* “braço” conectando o card ao trilho */}
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 h-0.5 bg-gradient-to-r ${
                          isLeft ? "right-0 w-24 from-sky-300 to-sky-600" : "left-0 w-24 from-sky-600 to-sky-300"
                        }`}
                      />
                      {/* pino */}
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full border-2 border-sky-400 shadow-lg ${
                          isLeft ? "right-24" : "left-24"
                        }`}
                      />

                      <Card className="hover:shadow-2xl transition-all duration-300 border-0 shadow-lg bg-white/95 backdrop-blur-sm">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-sky-200 rounded-xl flex items-center justify-center text-3xl shadow-lg border border-sky-200">
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <span className="bg-gradient-to-r from-sky-600 to-sky-700 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg inline-block mb-3">
                                {item.month}
                              </span>
                              <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">{item.title}</h3>
                              <p className="text-gray-600 leading-relaxed">{item.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
